import {
  readinessCheckinSchema,
  type ReadinessCheckin,
  type SorenessArea,
  type SymptomCodeName,
} from '@cadencia/contracts';
import {
  READINESS_SCORE_VERSION,
  computeReadiness,
  readinessTrend,
  type ReadinessInput,
  type ReadinessTrend,
} from '@cadencia/domain';
import { db } from '../db.js';
import { nowIso } from '../ids.js';
import { todayLocal } from '../../lib/dates.js';
import { newSyncColumns, putNewRows, putRow, type WriteContext } from '../writes.js';

export async function getCheckinForDate(
  userId: string,
  date: string,
): Promise<ReadinessCheckin | undefined> {
  const rows = await db.readinessCheckins.where('forDate').equals(date).toArray();
  return rows.find((row) => row.userId === userId && row.deletedAt === null);
}

export async function saveReadinessCheckin(
  ctx: WriteContext,
  input: ReadinessInput & {
    sorenessAreas: SorenessArea[];
    reportedSymptoms?: SymptomCodeName[];
    forDate?: string;
  },
): Promise<ReadinessCheckin> {
  const forDate = input.forDate ?? todayLocal();
  const existing = await getCheckinForDate(ctx.userId, forDate);
  if (existing) {
    /*
     * Un check-in por día, pero lo que se AVISA después no se descarta.
     *
     * Hasta el 23/09/2026 se devolvía el existente tal cual: quien volvía a la
     * pantalla el mismo día y marcaba dolor en el pecho no dejaba rastro del
     * síntoma, y la seguridad seguía leyendo el check-in de la mañana. Ahora
     * los síntomas y las zonas nuevas se SUMAN al del día; la puntuación y las
     * escalas no se tocan, porque describen cómo se empezó el día.
     */
    const sintomas = [
      ...new Set([...existing.reportedSymptoms, ...(input.reportedSymptoms ?? [])]),
    ];
    const clave = (a: SorenessArea): string => `${a.region}:${a.kind}`;
    const previas = new Set(existing.sorenessAreas.map(clave));
    const zonas = [
      ...existing.sorenessAreas,
      ...input.sorenessAreas.filter((area) => !previas.has(clave(area))),
    ];
    const nada =
      sintomas.length === existing.reportedSymptoms.length &&
      zonas.length === existing.sorenessAreas.length;
    if (nada) return existing;
    return putRow(
      ctx,
      'readinessCheckins',
      readinessCheckinSchema.parse({ ...existing, reportedSymptoms: sintomas, sorenessAreas: zonas }),
    );
  }

  // La puntuacion se calcula en el dominio y se ALMACENA con su version, para
  // que un cambio de formula no reescriba la historia.
  const score = computeReadiness(input);

  const row = readinessCheckinSchema.parse({
    ...newSyncColumns(ctx),
    checkinAt: nowIso(),
    forDate,
    sleepHours: input.sleepHours,
    sleepQuality: input.sleepQuality,
    energy: input.energy,
    motivation: input.motivation,
    stress: input.stress,
    soreness: input.soreness,
    sorenessAreas: input.sorenessAreas,
    reportedSymptoms: input.reportedSymptoms ?? [],
    readinessScore: score,
    scoreVersion: READINESS_SCORE_VERSION,
  });

  await putNewRows(ctx, [{ table: 'readinessCheckins', row }]);
  return row;
}

export async function getRecentCheckins(
  userId: string,
  limit = 28,
): Promise<ReadinessCheckin[]> {
  const rows = await db.readinessCheckins.where('userId').equals(userId).toArray();
  return rows
    .filter((row) => row.deletedAt === null)
    .sort((a, b) => b.forDate.localeCompare(a.forDate))
    .slice(0, limit);
}

/**
 * Los check-in de un rango de fechas, de antiguo a nuevo.
 *
 * No vale `getRecentCheckins` con un tope: ese devuelve los N mas recientes, y
 * recomponer el check-in de una semana pasada con suficientes dias por delante
 * se quedaria sin ninguna de sus filas.
 */
export async function listCheckinsBetween(
  userId: string,
  desde: string,
  hasta: string,
): Promise<ReadinessCheckin[]> {
  const rows = await db.readinessCheckins.where('userId').equals(userId).toArray();
  return rows
    .filter((row) => row.deletedAt === null && row.forDate >= desde && row.forDate <= hasta)
    .sort((a, b) => a.forDate.localeCompare(b.forDate));
}

export async function getReadinessTrend(userId: string): Promise<ReadinessTrend> {
  const recent = await getRecentCheckins(userId);
  return readinessTrend(recent.map((row) => ({ at: row.checkinAt, score: row.readinessScore })));
}
